/*******************************************************************************
 * Copyright (c) 2023 GetChkd
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 2.0.0
 *
 *******************************************************************************/
import React, { useState, useRef } from 'react';
import { 
    Grid, 
    CardContent, 
    FormControl, 
    Button, 
    FormHelperText, 
    Avatar 
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import Resizer from 'react-image-file-resizer';
import { getUserInfo } from '../../../../../../store/reducers/user/userAction';
import { patchUserMe } from '../../../../../../services/user/UserController';
import { IMAGES } from '../../../../../../config/assets';

const useStyles = makeStyles()(theme => ({
    formInput: {
        ...theme.typography.customInput,
        marginBottom: '10px !important'
    },
    avatar: {
        margin: 'auto',
        width: '150px!important',
        height: '150px!important'
    },
}));

const UserUpdateForm = ({ toggleEdit }) => {
    const fileLoaderRef = useRef(null);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);
    const [avatar, setAvatar] = useState('');

    // Handle file selection and resize the image
    const onLoadFile = async (file) => {
        if (file) {
            await resizeFile(file);
        }
    };

    // Resize image to a specific format and set as avatar
    const resizeFile = (file) => {
        return new Promise((resolve) => {
            Resizer.imageFileResizer(
                file,
                300,
                300,
                'JPEG',
                300,
                0,
                (uri) => {
                    resolve(uri);
                    setAvatar(uri);
                },
                'base64'
            );
        });
    };

    return (
        <CardContent>
            <Formik
                initialValues={{
                    username: user.username || '',
                    firstname: user.firstname || '',
                    lastname: user.lastname || '',
                    email: user.email || '',
                    cellphone: user.cellphone || '',
                }}
                validationSchema={Yup.object().shape({
                    username: Yup.string(),
                    firstname: Yup.string(),
                    lastname: Yup.string(),
                    email: Yup.string().email(),
                    cellphone: Yup.string(),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        await patchUserMe({
                            firstname: values.firstname,
                            lastname: values.lastname,
                            email: values.email,
                            cellphone: values.cellphone,
                            useravatar: avatar || user.useravatar,
                        });
                        toast.info('User information updated successfully');
                        await dispatch(getUserInfo()).unwrap();
                        toggleEdit(false);
                    } catch (error) {
                        toast.warning('Something went wrong, please try again later');
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, handleBlur, handleChange, touched, errors }) => (
                    <Form>
                        <Grid container spacing={4}>
                            {/* Avatar Upload Section */}
                            <Grid item xs={12} md={6} className={classes.cardBox}>
                                <Grid container spacing={4} justifyContent="center" alignItems="center">
                                    <Grid item xs={12} md={12} style={{ marginTop: '60px', textAlign: 'center' }}>
                                        <input
                                            ref={fileLoaderRef}
                                            style={{ display: 'none' }}
                                            type="file"
                                            onChange={(e) => onLoadFile(e.target.files[0])}
                                        />
                                        <Avatar
                                            src={avatar || user.useravatar || IMAGES.AVATAR_DEFAULT}
                                            className={classes.avatar}
                                            onClick={() => fileLoaderRef.current.click()}
                                        />
                                        <Button
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => fileLoaderRef.current.click()}
                                            style={{ marginTop: '20px' }}
                                        >
                                            Upload Avatar
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            
                            {/* User Information Fields */}
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={4}>
                                    {['username', 'firstname', 'lastname', 'email', 'cellphone'].map((field, index) => (
                                        <Grid key={index} item xs={12} sm={6}>
                                            <FormControl fullWidth className={classes.formInput}>
                                                <Field
                                                    id={`outlined-${field}`}
                                                    type="text"
                                                    value={values[field]}
                                                    variant="outlined"
                                                    name={field}
                                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    component={TextField}
                                                />
                                                {touched[field] && errors[field] && (
                                                    <FormHelperText error>
                                                        {errors[field]}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                        
                        {/* Form Actions */}
                        <Grid container   spacing={2} style={{ marginTop: '20px' }}>
                            <Grid item>
                                <Button type="submit" variant="contained" color="primary">Save</Button>
                            </Grid>
                            <Grid item>
                                <Button variant="outlined" onClick={() => toggleEdit(false)}>Cancel</Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </CardContent>
    );
};

export default UserUpdateForm;
